/* eslint-disable react-hooks/rules-of-hooks */
import React, { Fragment, useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Divider, InpockView, Paragraph2, SmallText } from 'inpock-ui-react';
import Cookies from 'js-cookie';
import Axios, { AxiosError } from 'axios';
import queryString from 'query-string';

import { useAuth } from '@/hooks/useAuth';
import { Icon, PageTitle } from '@/components';
import { useDialog, useOrderSheetMutation, useToast } from '@/hooks';
import {
  CashReceiptUsage,
  GetCustomerResponse,
  GetLatestAddressResponse,
  OrderSheetParameter,
  api,
  현금영수증_발행용도,
  ShippingType,
  PaymentMethod,
  SHIPPING_TYPE,
  CreateOrderParameters,
  MARKET_STATUS,
  InterestFreeCardsType,
  OrderSheetResponse,
  OrderDataList,
} from '@/apis';
import {
  ORDER_TEMPLATE,
  createOrder,
  getLocalStorageOrderSheet,
  getLocationState,
  notifyToSlackChannel,
  openPopup,
  safeLocalStorage,
  updateTermsForm,
  validateCashReceipt,
} from '@/utils';
import { Sections } from '@/fragments';
import {
  BACKUP_CHECKOUT_INFO,
  CHECKOUT_TERMS,
  CHECKOUT_VERSION,
  PAYMENT_METHOD,
} from '@/constants';
import 토스페이먼츠_에러_발생 from '@/utils/templates/slack';
import { useSectionContext } from '@/components/checkout/SectionContext';
import { resolveMarketClientKey } from '@/utils/marketKey';

import * as S from './styled';

export interface TFormPageProductInfo {
  title: string;
  price: number;
  shipping_fee: number;
  description: string;
  images: { image: string }[];
  link: {
    id: number;
    is_open: boolean;
    title: string;
  };
}

interface OrderPriceSummary {
  totalCount: number;
  totalPrice: number;
  totalShippingFee: number;
  totalCost: number;
  totalDiscount: number | null;
}

export function CheckoutPage() {
  const { REACT_APP_LINK_CLIENT_URL } = process.env;
  const location = useLocation();
  const navigate = useNavigate();

  const { version, from_cart } = queryString.parse(location.search);

  const toastOn = useToast();
  const [dialogOn, dialogOff] = useDialog();
  const { loggedIn } = useAuth();

  const { mutate: orderSheetMutation } = useOrderSheetMutation(version as string);

  const locationValue: OrderSheetParameter | null =
    getLocationState(location) || getLocalStorageOrderSheet(loggedIn);

  // locationValue 비어있을 경우 구매자 메뉴 페이지로 이동
  if (!locationValue || !locationValue.order_data) {
    window.location.href = `${REACT_APP_LINK_CLIENT_URL}/customer/menu`;
    return <></>;
  }

  const [customer, setCustomer] = useState<GetCustomerResponse & GetLatestAddressResponse>();
  const [isCustomerFetching, setIsCustomerFetching] = useState(true);

  const isInstagram = navigator.userAgent.match(/instagram/i);

  const [orderDataList, setOrderDataList] = useState<OrderDataList[]>([]);

  const [interestFreeCards, setInterestFreeCards] = useState<InterestFreeCardsType[]>([]);
  const [taxFreePrice, setTaxFreePrice] = useState<number>(0);
  const [shippingType, setShippingType] = useState<ShippingType>(SHIPPING_TYPE.GENERAL);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [orderPriceSummary, setOrderPriceSummary] = useState<OrderPriceSummary>({
    totalCount: 0,
    totalPrice: 0,
    totalShippingFee: 0,
    totalDiscount: 0,
    totalCost: 0,
  });

  /** form 상태 관련 */
  const [buyerInfo, setBuyerInfo] = useState({
    buyer_name: '',
    buyer_phone: '',
    buyer_email: '',
    isCertificated: false,
  });

  const [refundInfo, setRefundInfo] = useState({
    refund_account: '',
    refund_bank_code: '',
    refund_account_holder: '',
  });
  const [cardInfo, setCardInfo] = useState({
    cardCompany: '',
    cardInstallmentPlan: '0',
  });

  const [addressInfo, setAddressInfo] = useState({
    receiver_name: '',
    receiver_phone: '',
    receiver_postcode: '',
    receiver_address: '',
    receiver_detail_address: '',
    delivery_message: '',
  });

  const [termsForm, setTermsForm] = useState({
    terms_comfirm: true,
    privacy_confirm: true,
    order_confirm: true,
    years_confirm: true,
    non_members_confirm: true,
  });

  const [cashReceiptTypeSelected, setCashReceiptTypeSelected] = useState<string>('personal_phone');
  const [cashReceiptUsage, setCashReceiptUsage] = useState<현금영수증_발행용도>(
    CashReceiptUsage.미발행,
  );
  const [cashReceiptForm, setCashReceiptForm] = useState<{
    cash_receipt_type: null | string;
    cash_receipt_number: null | string;
  }>({
    cash_receipt_type: 'personal_phone',
    cash_receipt_number: '',
  });

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PAYMENT_METHOD.CARD);
  const [marketInfoLoaded, setMarketInfoLoaded] = useState(false);
  const [marketInfo, setMarketInfo] = useState<{
    key: string;
    key_encrypted?: boolean;
    status: MARKET_STATUS | null;
  }>({
    key: '',
    status: null,
  });

  const { buyerSectionRef, addressSectionRef, cashReceiptSectionRef } = useSectionContext();

  const 구매_필드 = {
    buyer: {
      buyer_name: {
        value: buyerInfo.buyer_name,
        message: '구매자 이름을 입력해주세요',
        ref: buyerSectionRef,
      },
      buyer_email: {
        value: buyerInfo.buyer_email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerInfo.buyer_email),
        message: '구매자 이메일 주소를 확인해주세요',
        ref: buyerSectionRef,
      },
      buyer_phone: {
        value: buyerInfo.buyer_phone,
        message: '구매자 전화번호를 입력해주세요',
        ref: buyerSectionRef,
      },
      cert_number: {
        value: loggedIn || buyerInfo.isCertificated,
        message: '구매자 인증을 완료해주세요',
        ref: buyerSectionRef,
      },
    },
    address: {
      receiver_name: {
        value: addressInfo.receiver_name,
        message: '수령인 이름을 입력해주세요',
        ref: addressSectionRef,
      },
      receiver_phone: {
        value: addressInfo.receiver_phone,
        message: '수령인 전화번호를 입력해주세요',
        ref: addressSectionRef,
      },
      receiver_postcode: {
        value: addressInfo.receiver_postcode,
        message: '우편번호를 입력해주세요',
        ref: addressSectionRef,
      },
      receiver_address: {
        value: addressInfo.receiver_address,
        message: '주소를 입력해주세요',
        ref: addressSectionRef,
      },
      receiver_detail_address: {
        value: addressInfo.receiver_detail_address,
        message: '상세주소를 입력해주세요',
        ref: addressSectionRef,
      },
    },
    cashReceipt: {
      cash_receipt_number: {
        value: validateCashReceipt(
          cashReceiptUsage,
          cashReceiptTypeSelected,
          cashReceiptForm.cash_receipt_number,
        ),
        message: '현금영수증 정보를 확인해주세요',
        ref: cashReceiptSectionRef,
      },
    },
  };

  const loginExpiredModal = () => {
    const { REACT_APP_LINK_CLIENT_URL } = process.env;

    return dialogOn({
      template: ORDER_TEMPLATE.campaign.signin(),
      close: {
        text: '로그인하기',
        type: 'primary',
        onClick: () => {
          safeLocalStorage.set('checkoutInfo', JSON.stringify(locationValue));

          Cookies.remove('customer_token');
          window.location.href = `${REACT_APP_LINK_CLIENT_URL}/customer/login?redirect=${encodeURIComponent(
            '/checkout?payment=true',
          )}`;
        },
      },
    });
  };

  useEffect(() => {
    if (!loggedIn) return setIsCustomerFetching(false);

    const fetchCustomerData = async () => {
      try {
        const [user, address] = await Promise.all([api.customer.get(), api.customer.address()]);
        const { cash_receipt_number, cash_receipt_type } = user;

        setCustomer({ ...user, ...address });
        if (address.shipping_type) {
          setShippingType(address.shipping_type);
        }

        /* 현금영수증 최근 구매 기록 자동 반영 */
        if (cash_receipt_number && cash_receipt_type) {
          const newUsage =
            cash_receipt_type === 'business_num'
              ? CashReceiptUsage.지출증빙용
              : CashReceiptUsage.소득공제용;

          setCashReceiptUsage(newUsage);
          setCashReceiptTypeSelected(cash_receipt_type);
          setCashReceiptForm({
            cash_receipt_type,
            cash_receipt_number,
          });
        } else {
          setCashReceiptUsage(CashReceiptUsage.미발행);
          setCashReceiptTypeSelected('personal_phone');
          setCashReceiptForm({
            cash_receipt_type: null,
            cash_receipt_number: null,
          });
        }
      } catch (error) {
        if (Axios.isAxiosError(error) && error.response?.status === 401) {
          loginExpiredModal();
        }
      } finally {
        setIsCustomerFetching(false);
      }
    };

    fetchCustomerData();
  }, [loggedIn]);

  const notifyOrderSheet = ({ message = '', to = `/${locationValue?.seller}` }) => {
    toastOn(message, 2000);
    navigate(version ? `/v2${to}` : to);
  };

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        market_id: locationValue?.market_id as number,
        shipping_type: shippingType,
        order_data: locationValue?.order_data || [],
      };

      orderSheetMutation(payload, {
        onSuccess: (data: OrderSheetResponse) => {
          const {
            discount_price,
            interest_free_cards,
            shipping_fee,
            total_price,
            tax_free_price,
            order_data_list,
          } = data;

          setOrderDataList(order_data_list);
          setTaxFreePrice(tax_free_price);
          setInterestFreeCards(interest_free_cards);
          setOrderPriceSummary({
            totalCount: order_data_list.length,
            totalPrice: total_price,
            totalShippingFee: shipping_fee,
            totalDiscount: discount_price,
            totalCost: total_price + shipping_fee - discount_price,
          });
        },
        onError: (error: AxiosError) => {
          const errorCode = (error.response?.data as { code?: string })?.code;
          if (error.response?.status === 401) return;

          switch (errorCode) {
            case 'INVALID_VALUE':
              notifyOrderSheet({
                message: '선택하신 옵션을 확인할 수 없어요 다시 선택해주세요 😢',
              });
              break;
            case 'NOT_ENOUGH_STOCK':
              notifyOrderSheet({
                message: '재고가 모두 떨어졌어요 다른 옵션을 선택해주세요 😢',
              });
              break;
            case 'NOT_AVAILABLE_REMOTE_AREA':
              dialogOn({
                template: ORDER_TEMPLATE.campaign.remote_area(
                  shippingType === SHIPPING_TYPE.JEJU ? '제주지역' : '도서산간지역',
                ),
                close: {
                  onClick: () => {
                    dialogOff();
                    setSubmitting(false);
                  },
                },
              });
              break;
            default:
              notifyOrderSheet({
                message: '상품 구매 중 문제가 발생했어요 잠시 후 다시 시도해주세요 😢',
              });
              break;
          }
        },
      });
    };

    fetchData();
  }, [shippingType, location.state, setOrderPriceSummary]);

  // 구매 페이지 이동 시 세션스토리지에 저장된 캠페인 id 삭제
  useEffect(() => {
    sessionStorage.removeItem(BACKUP_CHECKOUT_INFO);
  }, []);

  useEffect(() => {
    const fetchMarketInfo = async () => {
      try {
        const data = await api.market.getMarketKey(locationValue?.market_id as number);
        setMarketInfo(data);
      } catch (error) {
        console.error(error);
      } finally {
        setMarketInfoLoaded(true);
      }
    };
    if (version) {
      fetchMarketInfo();
    } else {
      setMarketInfoLoaded(true);
    }
  }, [version]);

  const 구매가능체크 = (payload: CreateOrderParameters) => {
    const validateField = (
      field: string,
      message: string,
      ref: { current: { [x: string]: { validate: () => void; focus: () => void } } },
    ) => {
      toastOn(message, 2000);
      notifyToSlackChannel(
        토스페이먼츠_에러_발생(
          payload,
          paymentMethod,
          message,
          Cookies.get('customer_token'),
          '마켓_결제_bot',
        ),
      );
      if (ref.current[field]) {
        ref.current[field].focus();
        ref.current[field].validate();
      }
      return false;
    };

    type SectionKeys = keyof typeof 구매_필드;
    type FieldKeys<T extends SectionKeys> = keyof (typeof 구매_필드)[T];

    for (const sectionKey in 구매_필드) {
      const section = sectionKey as SectionKeys;
      for (const fieldKey in 구매_필드[section]) {
        const field = fieldKey as FieldKeys<SectionKeys>;
        const { value, message, ref } = 구매_필드[section][field];
        if (!value) {
          validateField(field, message, ref);
          return false;
        }
      }
    }

    return true;
  };

  /** 결제 validation 포함한 모든 결제 관련 로직을 해당 createOrder에서 관리합니다 */
  const 결제하기 = async () => {
    if (submitting) return;
    // 결제 실패를 대비하기 위해 현제 결제 정보를 저장
    sessionStorage.setItem(BACKUP_CHECKOUT_INFO, JSON.stringify(locationValue));
    if (version) {
      sessionStorage.setItem(CHECKOUT_VERSION, version as string);
    }
    setSubmitting(true);

    const payload = {
      market_id: locationValue?.market_id as number,
      paymentMethod,
      order_data: locationValue?.order_data || [],
      ...buyerInfo,
      ...addressInfo,
      cash_receipt_type: null,
      cash_receipt_number: null,
      shipping_type: shippingType,
      refund_info: refundInfo,
      tax_free_price: taxFreePrice,
      from_cart: from_cart === 'true',

      /* 환불계좌 유효성 검사를 위한 필드 입니다.
       * 가상계좌일 경우, true
       * true일 경우, 백엔드에서 환불계좌에 대한 유효성 검사를 진행
       */
      is_cash: paymentMethod === PAYMENT_METHOD.VIRTUAL_ACCOUNT,
    };

    switch (cashReceiptUsage) {
      case CashReceiptUsage.미발행: {
        Object.assign(payload, {
          cash_receipt_type: null,
          cash_receipt_number: null,
        });
        break;
      }
      case CashReceiptUsage.소득공제용: {
        Object.assign(payload, {
          cash_receipt_type: cashReceiptTypeSelected,
          cash_receipt_number: cashReceiptForm.cash_receipt_number,
        });
        break;
      }
      case CashReceiptUsage.지출증빙용: {
        Object.assign(payload, {
          cash_receipt_type: 'business_num',
          cash_receipt_number: cashReceiptForm.cash_receipt_number,
        });
        break;
      }
    }

    try {
      if (!구매가능체크(payload)) {
        setSubmitting(false);
        return;
      }
      if (!version) {
        await createOrder(payload, cardInfo);
      } else {
        const marketClientKey = resolveMarketClientKey(marketInfo);
        await createOrder(payload, cardInfo, version as string, marketClientKey);
      }
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        const { response } = error;
        if (response) {
          const { status, data } = response;
          switch (data.code) {
            case 'NOT_ENOUGH_STOCK':
              dialogOn({
                template: ORDER_TEMPLATE.campaign.not_enough_stock(),
                close: {
                  onClick: () => {
                    dialogOff();
                    setSubmitting(false);
                  },
                },
              });
              break;
            case 'NOT_AVAILABLE_REMOTE_AREA':
              dialogOn({
                template: ORDER_TEMPLATE.campaign.remote_area(
                  shippingType === SHIPPING_TYPE.JEJU ? '제주지역' : '도서산간지역',
                ),
                close: {
                  onClick: () => {
                    dialogOff();
                    setSubmitting(false);
                  },
                },
              });
              break;
            case 'INVALID_VALUE':
              return toastOn(data.message);
            default:
              dialogOn({
                template: ORDER_TEMPLATE.campaign.invalid(),
                confirm: {
                  text: '문의하기',
                  type: 'secondary',
                  onClick: async () => {
                    dialogOff();
                    openPopup('https://inpocklink.channel.io/home', 375, 812);
                  },
                },
                close: {
                  onClick: () => {
                    dialogOff();
                    setSubmitting(false);
                  },
                },
              });
          }
          if (status === 401) {
            loginExpiredModal();
          }
        }
      } else {
        const errorText = String(error);
        const REQUIRED_CARD_INFO =
          '인증 창을 먼저 띄우려면 카드사 코드 또는 은행 코드 또는 간편결제 수단이 같이 전달되어야 합니다.';
        const ignoreErrorMsg = [
          '취소되었습니다.',
          '결제가 취소되었습니다.',
          '카드 결제 정보를 선택해주세요.',
          '환불계좌 정보를 모두 입력해주세요.',
          REQUIRED_CARD_INFO,
        ];

        // if (!ignoreErrorMsg.includes(errorText))
        //   notifyToSlackChannel(
        //     토스페이먼츠_에러_발생(
        //       payload,
        //       paymentMethod,
        //       errorText,
        //       Cookies.get('customer_token'),
        //     ),
        //   );

        const formattedErrorText =
          errorText === REQUIRED_CARD_INFO ? '카드사를 선택해주세요' : errorText;
        if (formattedErrorText) toastOn(formattedErrorText, 2000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const areAllTermsAccepted = Object.values(termsForm).every((v) => v);

  function BottomContent() {
    return (
      <>
        <Button
          className="mb-12"
          type="primary"
          fullWidth
          size="large"
          loading={submitting}
          disabled={submitting}
          onClick={결제하기}
        >
          {orderPriceSummary.totalCost.toLocaleString()}원 결제하기
        </Button>

        <S.TermsContainer
          onClick={() => {
            areAllTermsAccepted
              ? setTermsForm(updateTermsForm(false))
              : setTermsForm(updateTermsForm(true));
          }}
        >
          <Icon icon="Check" size={12} color={areAllTermsAccepted ? '#FF5B1A' : '#CFCFCF'} />
          <SmallText className="text-gray-600">
            {loggedIn && customer && '회원'} 본인은{' '}
            {generateLinks(
              CHECKOUT_TERMS(locationValue?.seller as string)[
                loggedIn && customer ? 'customer' : 'nonCustomer'
              ],
              dialogOn,
            )}
            에 동의합니다.
          </SmallText>
        </S.TermsContainer>
      </>
    );
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>결제하기 | 인포크링크</title>
          <meta property="og:title" content="인포크링크 - 가장 편리한 멀티링크 서비스" />
          <meta
            property="og:subscription"
            content="다양한 링크를 모으고 멋진 이야기를 들려주세요!"
          />
        </Helmet>
      </HelmetProvider>
      <InpockView>
        {isInstagram && (
          <S.InstagrmAlert>
            <SmallText className="text-white">
              🔔 원활한 서비스 이용을 위해 크롬, Safari 등 브라우저로 이동해주세요.
            </SmallText>
          </S.InstagrmAlert>
        )}
        <div className={`container ${isInstagram ? 'mt-30' : ''}`}>
          <PageTitle title="결제하기" top={isInstagram ? 30 : 0} />
          {orderDataList && orderDataList.length > 0 && (
            <Sections.Orderlines orderDataLists={orderDataList} version={version} />
          )}
          <Divider wide spacing={[20, 0]} />
          {!isCustomerFetching && (
            <>
              <Sections.Buyer
                form={buyerInfo}
                setForm={setBuyerInfo}
                customer={customer}
                isOpen={!customer?.email || !customer.name || !customer.phone}
              />
              <Divider wide />
              <Sections.Address
                form={addressInfo}
                buyerInfo={buyerInfo}
                setForm={setAddressInfo}
                shippingType={shippingType}
                setShippingType={setShippingType}
                customer={customer}
                isOpen={
                  !customer?.receiver_name ||
                  !customer?.receiver_phone ||
                  !customer?.receiver_postcode ||
                  !customer?.receiver_address ||
                  !customer?.receiver_detail_address
                }
              />
              <Divider wide />
              {!version && (
                <Sections.CashReceipt
                  form={cashReceiptForm}
                  setForm={setCashReceiptForm}
                  cashReceiptUsage={cashReceiptUsage}
                  setCashReceiptUsage={setCashReceiptUsage}
                  cashReceiptTypeSelected={cashReceiptTypeSelected}
                  setCashReceiptTypeSelected={setCashReceiptTypeSelected}
                />
              )}
            </>
          )}
          <Sections.PaymentMethod
            form={paymentMethod}
            setForm={setPaymentMethod}
            refundInfo={refundInfo}
            setRefundInfo={setRefundInfo}
            cardInfo={cardInfo}
            setCardInfo={setCardInfo}
            totalCost={orderPriceSummary.totalCost}
            interestFreeCards={interestFreeCards}
            marketStatus={marketInfo.status}
            is_v2={marketInfo.key !== ''}
            loading={!marketInfoLoaded}
          />

          <section className="mt-24 mb-30">
            <div id="payment-widget" />
          </section>
          <Divider wide />
          <Sections.Summary summaryInfo={orderPriceSummary} />
          <S.FixedBottom className="mt-24">
            <BottomContent />
          </S.FixedBottom>
        </div>
      </InpockView>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const generateLinks = (linksData: any[], dialogOn: (arg0: { template: JSX.Element }) => void) => {
  const handleClick = (e: React.MouseEvent, link: { url: string; text: string }) => {
    if (link.url === '#') {
      e.preventDefault();

      dialogOn({
        template: (
          <Paragraph2 style={{ textAlign: 'left' }}>
            에이비제트는 만 14세 이상의 고객에게 제공되는 서비스로서, 만 14세 미만 이용자는 주문
            서비스 이용이 어렵습니다.
          </Paragraph2>
        ),
      });
    } else {
      e.stopPropagation();
    }
  };

  return linksData.map((link, index) => (
    <Fragment key={index}>
      <a href={link.url} target="_blank" rel="noreferrer" onClick={(e) => handleClick(e, link)}>
        {link.text}
      </a>
      {index !== linksData.length - 1 && ', '}
    </Fragment>
  ));
};
